import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { api, extractApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth-context";
import { canCreateOrEditCRM, canSeeArchivedCRM } from "../lib/crm-permissions";
import PageHeader from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import StatusPill from "../components/status/StatusPill";
import EmptyState from "../components/empty/EmptyState";
import LeadFormDialog from "../components/leads/LeadFormDialog";
import {
  LEAD_STATUS_LABELS,
  LEAD_SOURCE_LABELS,
  LEAD_INTEREST_LABELS,
  PRIORITY_LABELS,
} from "../lib/labels";
import { LEAD_SOURCE, LEAD_INTEREST, PRIORITY } from "../lib/enums";
import { formatDate } from "../lib/format";
import { isLeadConverted } from "../lib/lead-conversion-state";
import { getLeadStatusFilterOptions } from "../lib/lead-status-options";

const ALL = "__all__";

export default function LeadsPage() {
  const navigate = useNavigate();
  const { role, isSuperAdmin } = useAuth();
  const perm = { role, isSuperAdmin };
  const canCreate = canCreateOrEditCRM(perm);
  const canSeeArchived = canSeeArchivedCRM(perm);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ status: ALL, source: ALL, interest: ALL, priority: ALL });
  const [showArchived, setShowArchived] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filters.status !== ALL) params.status = filters.status;
      if (filters.source !== ALL) params.source = filters.source;
      if (filters.interest !== ALL) params.interest = filters.interest;
      if (filters.priority !== ALL) params.priority = filters.priority;
      if (canSeeArchived && showArchived) params.include_archived = true;
      const r = await api.get("/api/leads", { params });
      setRows(r.data);
    } catch (e) {
      toast.error(extractApiError(e, "Leads konnten nicht geladen werden."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [filters.status, filters.source, filters.interest, filters.priority, showArchived]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    load();
  };

  const filterValueOrAll = (v) => v || ALL;

  return (
    <div>
      <PageHeader
        title="Leads"
        description="Anfragen, Interessenten und Vertriebsleads verwalten."
        actions={
          canCreate ? (
            <Button
              data-testid="lead-create-button"
              onClick={() => setDialogOpen(true)}
            >
              <Plus className="w-4 h-4" /> Lead erstellen
            </Button>
          ) : null
        }
      />

      <div className="surface rounded-xl p-3 mb-4 flex flex-col xl:flex-row xl:items-center gap-3">
        <form onSubmit={onSearchSubmit} className="relative flex-1 min-w-0 xl:min-w-[280px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="leads-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suche nach Titel, Kontakt, Firma …"
            className="pl-10"
          />
        </form>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:flex lg:gap-2">
          <FilterSelect
            testId="leads-filter-status"
            value={filters.status}
            onChange={(v) => setFilters((f) => ({ ...f, status: filterValueOrAll(v) }))}
            label="Status"
            options={getLeadStatusFilterOptions()}
          />
          <FilterSelect
            testId="leads-filter-source"
            value={filters.source}
            onChange={(v) => setFilters((f) => ({ ...f, source: filterValueOrAll(v) }))}
            label="Quelle"
            options={Object.values(LEAD_SOURCE).map((s) => ({ value: s, label: LEAD_SOURCE_LABELS[s] }))}
          />
          <FilterSelect
            testId="leads-filter-interest"
            value={filters.interest}
            onChange={(v) => setFilters((f) => ({ ...f, interest: filterValueOrAll(v) }))}
            label="Interesse"
            options={Object.values(LEAD_INTEREST).map((s) => ({ value: s, label: LEAD_INTEREST_LABELS[s] }))}
          />
          <FilterSelect
            testId="leads-filter-priority"
            value={filters.priority}
            onChange={(v) => setFilters((f) => ({ ...f, priority: filterValueOrAll(v) }))}
            label="Priorität"
            options={Object.values(PRIORITY).map((s) => ({ value: s, label: PRIORITY_LABELS[s].de }))}
          />
        </div>
        {canSeeArchived ? (
          <div className="flex items-center justify-start sm:justify-end">
            <label className="inline-flex select-none items-center gap-2 rounded-full border border-border/60 bg-background/70 px-2.5 py-1.5 text-sm text-foreground shadow-sm">
              <Switch
                checked={showArchived}
                onCheckedChange={setShowArchived}
                className="data-[state=checked]:border-sky-300 data-[state=checked]:bg-sky-500 dark:data-[state=checked]:border-sky-200 dark:data-[state=checked]:bg-sky-400 data-[state=unchecked]:border-slate-400/80 data-[state=unchecked]:bg-slate-300 dark:data-[state=unchecked]:border-slate-500/80 dark:data-[state=unchecked]:bg-slate-700 data-[state=checked]:shadow-[0_0_0_1px_rgba(255,255,255,0.18),0_0_16px_rgba(59,130,246,0.22)] dark:data-[state=checked]:shadow-[0_0_0_1px_rgba(255,255,255,0.14),0_0_18px_rgba(56,189,248,0.28)] data-[state=unchecked]:shadow-[0_0_0_1px_rgba(15,23,42,0.08)] dark:data-[state=unchecked]:shadow-[0_0_0_1px_rgba(255,255,255,0.06)] [&>span]:bg-white dark:[&>span]:bg-slate-100 data-[state=checked]:[&>span]:bg-white dark:data-[state=checked]:[&>span]:bg-sky-950"
              />
              <span>Archivierte anzeigen</span>
            </label>
          </div>
        ) : null}
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Wird geladen …</div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="Noch keine Leads"
          description="Erstellen Sie Ihren ersten Lead oder passen Sie die Filter an."
          actionLabel={canCreate ? "Lead erstellen" : undefined}
          onAction={canCreate ? () => setDialogOpen(true) : undefined}
          testId="leads-empty"
        />
      ) : (
        <div data-testid="leads-table" className="obsidian overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-max text-sm">
              <thead className="bg-muted/40 border-b border-border">
                <tr className="text-left text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Titel</th>
                  <th className="px-4 py-3 font-medium">Kontakt</th>
                  <th className="px-4 py-3 font-medium">Firma</th>
                  <th className="px-4 py-3 font-medium">Quelle</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Priorität</th>
                  <th className="px-4 py-3 font-medium">Erstellt</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((l) => {
                  const st = LEAD_STATUS_LABELS[l.status] || { de: l.status, tone: "muted" };
                  const pr = PRIORITY_LABELS[l.priority] || { de: l.priority, tone: "muted" };
                  const converted = isLeadConverted(l);
                  return (
                    <tr
                      key={l.id}
                      data-testid={`leads-table-row-${l.id}`}
                      onClick={() => navigate(`/leads/${l.id}`)}
                      className={`cursor-pointer transition-colors hover:bg-muted/50 ${
                        converted ? "opacity-80" : ""
                      }`}
                    >
                      <td className="px-4 py-3 font-medium">
                        <span className="inline-flex flex-wrap items-center gap-2">
                          {l.title}
                          {converted ? <Badge variant="secondary">Umgewandelt</Badge> : null}
                          {l.archived_at ? <Badge variant="secondary">Archiviert</Badge> : null}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{l.contact_name || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{l.company_name || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        <div>{LEAD_SOURCE_LABELS[l.source]}</div>
                        {l.interest ? (
                          <div className="text-xs text-muted-foreground">
                            Interesse: {LEAD_INTEREST_LABELS[l.interest]}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3"><StatusPill tone={st.tone}>{st.de}</StatusPill></td>
                      <td className="px-4 py-3"><StatusPill tone={pr.tone}>{pr.de}</StatusPill></td>
                      <td className="px-4 py-3 text-muted-foreground">{formatDate(l.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <LeadFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={() => {
          setDialogOpen(false);
          load();
        }}
      />
    </div>
  );
}

function FilterSelect({ value, onChange, label, options, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="w-full lg:w-44">
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{label}: Alle</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
